import React, { useEffect, useState, useCallback } from 'react';
import { Search, UserPlus, UserMinus, Phone, Loader2, Pencil } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  whatsappApi,
  WhatsAppRecipient,
} from '../../../services/whatsappApi';
import AddRecipientModal from './AddRecipientModal';
import EditRecipientModal from './EditRecipientModal';

interface WhatsAppRosterTableProps {
  clientId: string;
  channelId: string;
  refreshKey?: number;
}

type StatusFilter = 'all' | 'opted_in' | 'opted_out' | 'pending';

export const WhatsAppRosterTable: React.FC<WhatsAppRosterTableProps> = ({
  clientId,
  channelId,
  refreshKey = 0,
}) => {
  const [recipients, setRecipients] = useState<WhatsAppRecipient[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<WhatsAppRecipient | null>(null);
  const [reloadCount, setReloadCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await whatsappApi.listRecipients(clientId, {
        channel_id: channelId,
        search: search.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 200,
      });
      setRecipients(res.recipients);
      setTotal(res.total);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  }, [clientId, channelId, search, statusFilter]);

  useEffect(() => {
    load();
  }, [load, reloadCount, refreshKey]);

  const optOut = async (id: string) => {
    if (!window.confirm('Opt this recipient out of WhatsApp messages?')) return;
    try {
      await whatsappApi.optOutRecipient(clientId, id);
      toast.success('Opted out');
      setReloadCount((n) => n + 1);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to opt out');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Toolbar */}
      <div className="px-5 py-4 border-b border-gray-200 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Recipients</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {total} {total === 1 ? 'person' : 'people'} can interact with this bot on WhatsApp.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap"
        >
          <UserPlus size={15} />
          Add recipient
        </button>
      </div>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
        </div>
        <div className="flex gap-1">
          {(['all', 'opted_in', 'pending', 'opted_out'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 rounded-lg text-xs font-medium capitalize ${
                statusFilter === s
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="px-5 py-10 text-center text-sm text-gray-500">
          <Loader2 size={18} className="animate-spin inline-block mr-2" />
          Loading recipients…
        </div>
      ) : recipients.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-gray-500">
          No recipients {search || statusFilter !== 'all' ? 'matching your filters' : 'yet'}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Phone</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Tags</th>
                <th className="px-5 py-3">Last inbound</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recipients.map((r) => (
                <tr key={r.id}>
                  <td className="px-5 py-3 text-gray-900">{r.display_name || '—'}</td>
                  <td className="px-5 py-3 font-sans text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <Phone size={11} className="text-gray-400" />
                      {r.phone_e164}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill status={r.opt_in_status} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.tags.length === 0 ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        r.tags.map((t) => (
                          <span
                            key={t}
                            className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded"
                          >
                            {t}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500">
                    {r.last_inbound_at
                      ? new Date(r.last_inbound_at).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => setEditing(r)}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-brand-600"
                      >
                        <Pencil size={12} />
                        Edit
                      </button>
                      {r.opt_in_status !== 'opted_out' && (
                        <button
                          onClick={() => optOut(r.id)}
                          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                        >
                          <UserMinus size={12} />
                          Opt out
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddRecipientModal
        open={showAdd}
        clientId={clientId}
        channelId={channelId}
        onClose={() => setShowAdd(false)}
        onAdded={() => setReloadCount((n) => n + 1)}
      />

      <EditRecipientModal
        open={!!editing}
        clientId={clientId}
        recipient={editing}
        onClose={() => setEditing(null)}
        onSaved={() => setReloadCount((n) => n + 1)}
      />
    </div>
  );
};

const StatusPill: React.FC<{ status: WhatsAppRecipient['opt_in_status'] }> = ({ status }) => {
  const styles =
    status === 'opted_in'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'opted_out'
      ? 'bg-red-50 text-red-700'
      : 'bg-amber-50 text-amber-700';
  const label = status.replace('_', ' ');
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full capitalize ${styles}`}>
      {label}
    </span>
  );
};

export default WhatsAppRosterTable;
